import { useState } from 'react';
import { REGULATIONS, GORAMPE_RULES, WIZARD_STEPS, REGULATORY_PDFS } from './regulations-data';

type Section = 'regulations' | 'gorampe' | 'wizard' | 'pdfs';

export function RulesPanel() {
  const [openSection, setOpenSection] = useState<Section | null>('regulations');

  const sectionToggle = (s: Section) => setOpenSection(openSection === s ? null : s);

  return (
    <div className="space-y-3">
      {/* Section 1: Règles réglementaires */}
      <details
        open={openSection === 'regulations'}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <summary
          onClick={(e) => {
            e.preventDefault();
            sectionToggle('regulations');
          }}
          className="cursor-pointer select-none rounded-lg bg-slate-50 px-4 py-3 font-semibold text-slate-900 hover:bg-slate-100"
        >
          📋 Règles réglementaires (CCQ / CSA / SHQ)
        </summary>
        <div className="p-4">
          <p className="mb-3 text-sm text-slate-600">
            Seuils légaux applicables aux rampes d&apos;accessibilité au Québec et au Canada.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">Règle</th>
                  <th className="px-3 py-2">Valeur</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Usage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {REGULATIONS.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.title}</div>
                      {r.description && (
                        <div className="text-xs text-slate-500">{r.description}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700">{r.value}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.source}</td>
                    <td className="px-3 py-2">
                      <UsageBadge usage={r.usage || 'both'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </details>

      {/* Section 2: Règles GoRampe internes */}
      <details
        open={openSection === 'gorampe'}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <summary
          onClick={(e) => {
            e.preventDefault();
            sectionToggle('gorampe');
          }}
          className="cursor-pointer select-none rounded-lg bg-amber-50 px-4 py-3 font-semibold text-amber-900 hover:bg-amber-100"
        >
          ⚙️ Règles internes GoRampe
        </summary>
        <div className="p-4">
          <p className="mb-3 text-sm text-slate-600">
            Hiérarchie des pentes acceptées avec niveau de tolérance.
          </p>
          <div className="space-y-2">
            {GORAMPE_RULES.map((r) => (
              <div
                key={r.id}
                className="flex items-start gap-3 rounded border border-slate-200 p-3"
              >
                <FlagBadge flag={r.flag} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{r.title}</span>
                    <span className="font-mono text-sm text-slate-500">{r.value}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{r.rationale}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded bg-amber-50 p-3 text-xs text-amber-800">
            <strong>Note légale :</strong> La pente 1:8 dépasse le seuil légal CCQ pour usage PMR
            (1:12). GoRampe l&apos;accepte avec warning pour les configurations à usage non-PMR
            uniquement.
          </div>
        </div>
      </details>

      {/* Section 3: Procédure */}
      <details
        open={openSection === 'wizard'}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <summary
          onClick={(e) => {
            e.preventDefault();
            sectionToggle('wizard');
          }}
          className="cursor-pointer select-none rounded-lg bg-blue-50 px-4 py-3 font-semibold text-blue-900 hover:bg-blue-100"
        >
          🪜 Procédure de configuration (5 étapes)
        </summary>
        <div className="p-4">
          <ol className="space-y-3">
            {WIZARD_STEPS.map((step) => (
              <li key={step.id} className="flex gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                  {step.number}
                </span>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{step.title}</div>
                  <p className="mt-0.5 text-sm text-slate-600">{step.description}</p>
                  <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
                    {step.triggers.map((t, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-blue-500">→</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </details>

      {/* Section 4: Documents PDFs */}
      <details
        open={openSection === 'pdfs'}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <summary
          onClick={(e) => {
            e.preventDefault();
            sectionToggle('pdfs');
          }}
          className="cursor-pointer select-none rounded-lg bg-green-50 px-4 py-3 font-semibold text-green-900 hover:bg-green-100"
        >
          📄 Documents réglementaires (PDFs)
        </summary>
        <div className="p-4">
          <p className="mb-3 text-sm text-slate-600">
            Téléchargez les références officielles pour consultation hors-ligne.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {REGULATORY_PDFS.map((pdf) => (
              <a
                key={pdf.id}
                href={`/regulations/${pdf.filename}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 rounded-md border border-slate-200 p-3 transition hover:border-green-500 hover:bg-green-50"
              >
                <span className="text-2xl">📄</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-900">{pdf.title}</div>
                  <div className="text-xs text-slate-500">{pdf.description}</div>
                  <div className="mt-1 flex gap-1.5">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {pdf.source}
                    </span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {pdf.language.toUpperCase()}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </details>
    </div>
  );
}

function UsageBadge({ usage }: { usage: 'res' | 'com' | 'both' }) {
  const label = usage === 'res' ? 'Résidentiel' : usage === 'com' ? 'Commercial' : 'Tous';
  const cls =
    usage === 'res'
      ? 'bg-blue-100 text-blue-700'
      : usage === 'com'
        ? 'bg-purple-100 text-purple-700'
        : 'bg-slate-100 text-slate-700';
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function FlagBadge({ flag }: { flag: 'ok' | 'warn' | 'limit' | 'max-absolute' | 'error' }) {
  const map = {
    ok: { label: '✓', cls: 'bg-green-100 text-green-700' },
    warn: { label: '⚠', cls: 'bg-yellow-100 text-yellow-700' },
    limit: { label: '!', cls: 'bg-orange-100 text-orange-700' },
    'max-absolute': { label: '⚠⚠', cls: 'bg-red-100 text-red-700' },
    error: { label: '✕', cls: 'bg-red-200 text-red-900' },
  } as const;
  const m = map[flag];
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-bold ${m.cls}`}
    >
      {m.label}
    </span>
  );
}
